import {
  TimetablePrintColorOrigins,
  TimetablePrintColorPositions,
  TimetablePrintLayoutOptions,
  TimetablePrintPersonNameFormats,
  TimetableReportType,
} from '@bp/planung-graphql-types';
import {
  TimetablePrintBaseData,
  TimetablePrintLayoutData,
} from '../components/TimetablePrintConfiguration/forms/TimetablePrintConfigurationForm';

/**
 * Startwerte des Konfigurationsdialogs für einen noch nicht konfigurierten Report.
 *
 * Diese Werte MÜSSEN mit den Defaults der PDF-Erzeugung im Backend übereinstimmen
 * (`planung-backend`: `src/pdf/helpers/default-timetable-print-config.ts`), sonst zeigt der Dialog
 * etwas anderes an, als das erzeugte PDF tatsächlich verwendet. Bei Änderungen beide Seiten anpassen.
 */
export const timetablePrintColorDefaults: Record<TimetableReportType, Omit<TimetablePrintBaseData, 'color'>> = {
  singleTeacher: {
    colorOrigin: TimetablePrintColorOrigins.Class,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: true,
    printSubtitle: true,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  singleClass: {
    colorOrigin: TimetablePrintColorOrigins.Subject,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: true,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  singleRoom: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: true,
    printSubtitle: true,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  singleSubject: {
    colorOrigin: TimetablePrintColorOrigins.None,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  allTeachers: {
    colorOrigin: TimetablePrintColorOrigins.Class,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  allClasses: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  allRooms: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  allSubjects: {
    colorOrigin: TimetablePrintColorOrigins.None,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksTeacher: {
    colorOrigin: TimetablePrintColorOrigins.Class,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksClass: {
    colorOrigin: TimetablePrintColorOrigins.Subject,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksRoom: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksSubject: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksAllClasses: {
    colorOrigin: TimetablePrintColorOrigins.Subject,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  teachingBlocksAllClassesCombined: {
    colorOrigin: TimetablePrintColorOrigins.Subject,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  doorSigns: {
    colorOrigin: TimetablePrintColorOrigins.Teacher,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  attendanceSchedule: {
    colorOrigin: TimetablePrintColorOrigins.None,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
  schoolTrips: {
    colorOrigin: TimetablePrintColorOrigins.None,
    colorPosition: TimetablePrintColorPositions.Full,
    printClasses: false,
    scaleText: true,
    exactMinute: false,
    printSubtitle: false,
    printLogo: false,
    largeFont: true,
    showSchoolWeeks: false,
    showCalendarWeeks: true,
    showWeekSchoolDays: false,
    timeHorizontal: false,
    epochGridOwnPage: false,
    responsiblePersonNameFormat: TimetablePrintPersonNameFormats.DisplayNameShort,
  },
};

/** Layout-Startwerte; ebenfalls spiegelbildlich zu den Backend-Defaults zu halten. */
export const timetablePrintLayoutDefaults: Record<TimetableReportType, TimetablePrintLayoutData> = {
  singleTeacher: {
    topLeft: TimetablePrintLayoutOptions.ClassName,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  singleClass: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  singleRoom: {
    topLeft: TimetablePrintLayoutOptions.TeacherShort,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.ClassName,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  singleSubject: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.Empty,
    center: TimetablePrintLayoutOptions.Empty,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  allTeachers: {
    topLeft: TimetablePrintLayoutOptions.TeacherShort,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  allClasses: {
    topLeft: TimetablePrintLayoutOptions.TeacherShort,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  allRooms: {
    topLeft: TimetablePrintLayoutOptions.TeacherShort,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.ClassName,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  allSubjects: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.Empty,
    center: TimetablePrintLayoutOptions.Empty,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksTeacher: {
    topLeft: TimetablePrintLayoutOptions.ClassName,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksClass: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksRoom: {
    topLeft: TimetablePrintLayoutOptions.TeacherShort,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.ClassName,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksSubject: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.TeacherShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksAllClasses: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  teachingBlocksAllClassesCombined: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.GroupName,
    center: TimetablePrintLayoutOptions.SubjectShort,
    bottomLeft: TimetablePrintLayoutOptions.RoomNumber,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  doorSigns: {
    topLeft: TimetablePrintLayoutOptions.ClassAndGroup,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.Empty,
    center: TimetablePrintLayoutOptions.TeacherLastName,
    bottomLeft: TimetablePrintLayoutOptions.SubjectShort,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  attendanceSchedule: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.Empty,
    center: TimetablePrintLayoutOptions.Empty,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
  schoolTrips: {
    topLeft: TimetablePrintLayoutOptions.Empty,
    topCenter: TimetablePrintLayoutOptions.Empty,
    topRight: TimetablePrintLayoutOptions.Empty,
    center: TimetablePrintLayoutOptions.Empty,
    bottomLeft: TimetablePrintLayoutOptions.Empty,
    bottomCenter: TimetablePrintLayoutOptions.Empty,
    bottomRight: TimetablePrintLayoutOptions.Empty,
  },
};
